import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Home, Users, TrendingUp, FileText, MessageSquare, Settings, LogOut, Menu, X, Building2, UserCog, Wallet, Percent } from 'lucide-react';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getNavItems = () => {
    if (user?.role === 'super_admin') {
      return [
        { icon: Home, label: 'Dashboard', path: '/' },
        { icon: Building2, label: 'Mandali', path: '/admin/mandali' },
        { icon: UserCog, label: 'Users', path: '/admin/users' },
        { icon: Settings, label: 'Settings', path: '/settings' },
      ];
    } else if (user?.role === 'mandali_manager') {
      return [
        { icon: Home, label: 'Dashboard', path: '/' },
        { icon: Users, label: 'Members', path: '/manager/members' },
        { icon: TrendingUp, label: 'Loans', path: '/manager/loans' },
        { icon: Wallet, label: 'Lavajam Accounts', path: '/manager/lavajam' },
        { icon: Wallet, label: 'Collections', path: '/manager/collections' },
        { icon: Percent, label: 'Interest Dist.', path: '/manager/interest-distribution' },
        { icon: FileText, label: 'Reports', path: '/manager/reports' },
        { icon: Settings, label: 'Settings', path: '/settings' },
      ];
    } else {
      return [
        { icon: Home, label: 'Dashboard', path: '/' },
        { icon: Users, label: 'Profile', path: '/member/profile' },
        { icon: Settings, label: 'Settings', path: '/settings' },
      ];
    }
  };

  const navItems = getNavItems();

  // Smart page title resolution - handles sub-routes like /manager/members/123
  const getPageTitle = () => {
    const path = location.pathname;
    
    // First try exact match
    const exactMatch = navItems.find(item => item.path === path);
    if (exactMatch) return exactMatch.label;
    
    // Then try prefix match for sub-routes (e.g., /manager/members/123 → Members)
    const prefixMatch = navItems.find(item => 
      item.path !== '/' && path.startsWith(item.path)
    );
    if (prefixMatch) return prefixMatch.label;
    
    // Special case mappings for detail pages
    if (path.includes('/manager/members/')) return 'Members';
    if (path.includes('/manager/loans/')) return 'Loans';
    if (path.includes('/manager/lavajam/')) return 'Lavajam Accounts';
    
    return 'Dashboard';
  };

  return (
    <div className="min-h-screen flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 glass border-r transform transition-transform lg:translate-x-0 lg:static ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b bg-gradient-to-r from-emerald-50 to-teal-50">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_scalemicrofin/artifacts/zwx3wriv_scaleMicroFin-icon%27png.png"
                alt="Logo"
                className="h-12 w-12 drop-shadow-md"
              />
              <div>
                <span className="text-xl font-bold gradient-text block leading-tight">
                  JD Group
                </span>
                <span className="text-xs text-emerald-600 font-medium">
                  {user?.role ? user.role.replace('_', ' ').toUpperCase() : 'MEMBER'}
                </span>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1" data-testid="sidebar-nav">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all group relative overflow-hidden ${isActive
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-emerald-50'
                    }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {isActive && (
                    <div className="absolute inset-0 bg-white opacity-10 group-hover:opacity-20 transition-opacity"></div>
                  )}
                  <Icon size={20} className={isActive ? 'text-white' : 'text-gray-500 group-hover:text-emerald-600'} />
                  <span className={`font-medium ${isActive ? 'text-white' : ''}`}>{item.label}</span>
                  {isActive && (
                    <div className="ml-auto w-2 h-2 rounded-full bg-white animate-pulse"></div>
                  )}
                </Link>
              );
            })}
          </nav>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="glass border-b sticky top-0 z-30 shadow-soft" data-testid="header">
          <div className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden rounded-xl hover:bg-emerald-50"
                onClick={() => setSidebarOpen(!sidebarOpen)}
                data-testid="mobile-menu-button"
              >
                {sidebarOpen ? <X /> : <Menu />}
              </Button>
              <div>
                <h2 className="text-xl font-bold text-gray-800">
                  {getPageTitle()}
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Welcome back, {user?.name ? user.name.split(' ')[0] : 'User'}!</p>
              </div>
            </div>

            <div className="flex items-center gap-2">

              {/* User menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="flex items-center gap-2 rounded-xl hover:bg-emerald-50 pl-2 pr-3" data-testid="user-menu">
                    <Avatar className="h-9 w-9 ring-2 ring-emerald-100">
                      <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-teal-500 text-white font-semibold">
                        {user?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <span className="hidden md:inline font-medium text-gray-700">{user?.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56 rounded-xl border-0 shadow-lg">
                  <DropdownMenuLabel className="pb-2">
                    <div className="space-y-1">
                      <div className="font-semibold text-gray-800">{user?.name}</div>
                      <div className="text-xs text-gray-500">{user?.email}</div>
                      <div className="inline-block">
                        <span className="status-badge bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-[10px] px-2 py-1 mt-2">
                          {user?.role ? user.role.replace('_', ' ') : 'Member'}
                        </span>
                      </div>
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-button" className="rounded-lg text-red-600 hover:bg-red-50 hover:text-red-700">
                    <LogOut className="mr-2" size={16} />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 md:p-6 overflow-auto" data-testid="main-content">
          {children}
        </main>

        {/* Footer */}
        <footer className="py-3 px-4 border-t bg-gray-50/80 text-center">
          <p className="text-xs text-gray-500">
            Powered by <span className="font-semibold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">ScaleSync</span>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default DashboardLayout;
